import type { Operation } from 'fast-json-patch';
import {
  createQueryParameters,
  type DocumentMetadataParameters,
  type DocumentParameters,
  type FetchClient,
  type PageParameters,
} from './http';
import type { BaseMetadataDocument } from './types/common';
import type { ContentPage } from './types/contents';
import type { DocumentVersion, VersionsPage } from './types/documents';
import type { Right } from './types/rights';

export const createDocument = <T>({
  client,
  workspaceId,
  modelId,
  confidentialityLevel,
  destroyDate,
  contentsOutsideSGNetwork,
  metadata,
}: {
  client: FetchClient;
  workspaceId: string;
  modelId: string;
  metadata: T;
  confidentialityLevel?: string;
  destroyDate?: string;
  contentsOutsideSGNetwork?: boolean;
}): Promise<BaseMetadataDocument & { metadata: T }> => {
  const queryParameters = createQueryParameters({ confidentialityLevel, destroyDate, contentsOutsideSGNetwork });
  return client.post(`${client.baseUrl}/workspaces/${workspaceId}/documents${queryParameters ? '?' + queryParameters : ''}`, {
    modelId,
    metadata,
  });
};

export const copyDocument = <T>({
  client,
  workspaceId,
  documentId,
  versionId,
  toWorkspaceId,
  toModelId,
  useTargetMetadata,
  metadataToAdd,
  confidentialityLevel,
  destroyDate,
  contentsOutsideSGNetwork,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  versionId: string;
  toWorkspaceId: string;
  toModelId: string;
  useTargetMetadata: boolean;
  metadataToAdd: T;
  confidentialityLevel?: string;
  destroyDate?: string;
  contentsOutsideSGNetwork?: boolean;
}): Promise<BaseMetadataDocument & { metadata: T }> => {
  const queryParameters = createQueryParameters({
    toWorkspaceId,
    toModelId,
    useTargetMetadata,
    confidentialityLevel,
    destroyDate,
    contentsOutsideSGNetwork,
  });
  return client.post(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/copy${
      queryParameters ? '?' + queryParameters : ''
    }`,
    metadataToAdd
  );
};

export const deleteDocument = ({
  client,
  workspaceId,
  documentId,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
}): Promise<boolean> => {
  return client.del(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}`);
};

export const getDocumentVersions = ({
  client,
  workspaceId,
  documentId,
  page = '1',
  size = '20',
}: { client: FetchClient } & PageParameters & DocumentParameters): Promise<VersionsPage> => {
  return client.get(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions?page=${page}&size=${size}`);
};

export const getDocumentContents = ({
  client,
  workspaceId,
  documentId,
  versionId = 'last',
  withOCRRequests = false,
  page = '1',
  size = '20',
  abortSignal,
}: { client: FetchClient } & PageParameters &
  DocumentParameters & {
    versionId?: string;
    withOCRRequests?: boolean;
    abortSignal?: AbortSignal;
  }): Promise<ContentPage> => {
  return client.get(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/contents?page=${page}&size=${size}&withOCRRequests=${withOCRRequests}`,
    { signal: abortSignal }
  );
};

export const getDocumentMetadata = ({
  client,
  workspaceId,
  documentId,
  versionId = 'last',
}: { client: FetchClient } & DocumentParameters & { versionId?: string }): Promise<DocumentVersion<any>> => {
  return client.get(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/metadata`);
};

export const getDocumentRight = ({
  client,
  workspaceId,
  documentId,
  versionId = 'last',
}: { client: FetchClient } & DocumentParameters & { versionId?: string }): Promise<{
  documentVersionRight?: Right;
  modelRight?: Right;
}> => {
  return client.get(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/rights`);
};

export const applyRight = ({
  client,
  workspaceId,
  documentId,
  versionId,
  rightId,
}: {
  client: FetchClient;
  workspaceId: string;
  documentId: string;
  versionId: string;
  rightId: string;
}): Promise<Right> => {
  return client.put(`${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/${versionId}/rights`, { rightId });
};

export const updateDocumentMetadata = ({
  client,
  workspaceId,
  documentId,
  confidentialityLevel,
  destroyDate,
  contentsOutsideSGNetwork,
  metadataChanges,
}: { client: FetchClient } & DocumentMetadataParameters & { metadataChanges: Operation[] }): Promise<
  BaseMetadataDocument & { metadata: any }
> => {
  const queryParameters = createQueryParameters({ confidentialityLevel, destroyDate, contentsOutsideSGNetwork });
  return client.post(
    `${client.baseUrl}/workspaces/${workspaceId}/documents/${documentId}/versions/metadata${queryParameters ? '?' + queryParameters : ''}`,
    metadataChanges
  );
};
